import { CLASSES, DECADES, ORIGINS, RARITIES } from './carSchema';

/**
 * The garage's filters, as pure functions over the joined catalogue.
 *
 * Four facets, and every one of them is a closed vocabulary the schema already
 * owns (`carSchema.js`). That is what makes this module short: there is nothing
 * to band, nothing to score and nothing to threshold — a car either is a 90s
 * JDM icon or it is not — so a facet is a field, its vocabulary, and the two
 * message keys the composition `cars.class.${value}` implies.
 *
 * The rule, stated once because it is the only rule: OR within a facet, AND
 * across facets. Two decades widens; a decade and an origin narrows. Written the
 * other way round the row would empty itself the moment a second chip was
 * tapped, which is the single most common way a filter row is wrong.
 */

/**
 * The facets, in the order the sheet draws them.
 *
 * Ordered by how a reader thinks about these cars: WHEN first, because the
 * product is about an era; then WHAT it is, then WHERE it is from, then how hard
 * it is to find. Rarity is last deliberately — it is the only one that is a
 * claim rather than a fact, and it is the least useful thing to narrow by first.
 *
 * `vocabulary` is the schema's list rather than whatever the catalogue happens
 * to contain, so the options always come back in the SCHEMA's order — `80s` then
 * `90s`, never the order the content file was authored in. What the catalogue
 * contains decides which of them are OFFERED, and that is `listFacets`.
 */
export const FACETS = [
    { key: 'decade', vocabulary: DECADES, valueOf: (car) => car?.decade },
    { key: 'class', vocabulary: CLASSES, valueOf: (car) => car?.class },
    { key: 'origin', vocabulary: ORIGINS, valueOf: (car) => car?.origin },
    { key: 'rarity', vocabulary: RARITIES, valueOf: (car) => car?.rarity },
].map((facet) => ({
    ...facet,
    labelKey: `garage.filter.group.${facet.key}`,
    // Derived rather than stored: the value IS the last segment of its own key,
    // and storing the pair would be one more place for a vocabulary to drift.
    optionLabelKey: (value) => `cars.${facet.key}.${value}`,
}));

export const FACET_KEYS = FACETS.map((facet) => facet.key);

/** The unfiltered garage. Frozen: every operation below returns a new object. */
export const NO_FILTERS = Object.freeze(
    Object.fromEntries(FACET_KEYS.map((key) => [key, Object.freeze([])])),
);

function facetByKey(facetKey) {
    return FACETS.find((facet) => facet.key === facetKey) || null;
}

export function isFacetActive(filters, facetKey, value) {
    return (filters?.[facetKey] || []).includes(value);
}

/**
 * Add or remove one value, leaving every other facet exactly as it was.
 *
 * A key the model does not own is refused rather than merged in: a stray facet
 * would survive every operation here and match nothing, which reaches the reader
 * as a garage that has gone empty for no visible reason. The filters can arrive
 * from a URL (`application/cars/browseState.js`), so this is a boundary.
 */
export function toggleFacetValue(filters, facetKey, value) {
    if (!facetByKey(facetKey)) {
        return filters;
    }

    const selected = filters?.[facetKey] || [];

    return {
        ...filters,
        [facetKey]: selected.includes(value)
            ? selected.filter((entry) => entry !== value)
            : [...selected, value],
    };
}

export function countActiveFilters(filters) {
    return FACET_KEYS.reduce((total, key) => total + (filters?.[key]?.length || 0), 0);
}

export function matchesFilters(car, filters) {
    return FACETS.every((facet) => {
        const selected = filters?.[facet.key] || [];

        return selected.length === 0 || selected.includes(facet.valueOf(car));
    });
}

/**
 * The filtered garage — and the SAME ARRAY back when nothing is selected.
 *
 * Identity matters here: the deck decides whether the reader arrived from a
 * filtered shelf by asking whether this list is the catalogue, and the garage's
 * memoisation depends on an unfiltered render not producing a new array every
 * time a chip is drawn.
 */
export function filterCars(cars, filters) {
    return countActiveFilters(filters) === 0
        ? cars
        : (cars || []).filter((car) => matchesFilters(car, filters));
}

/**
 * The facets worth offering, given the catalogue in hand.
 *
 * An option no car satisfies is dropped, and a facet left with fewer than two
 * options is dropped whole — a filter with one choice is a label wearing a
 * control. No car of the 2000s has been authored yet, so today the decade facet
 * offers two options rather than three, and nothing had to say so: adding one is
 * a content operation, not a code change.
 */
export function listFacets(cars) {
    return FACETS
        .map(({ key, labelKey, vocabulary, valueOf, optionLabelKey }) => ({
            key,
            labelKey,
            options: vocabulary
                .map((value) => ({
                    value,
                    labelKey: optionLabelKey(value),
                    count: (cars || []).filter((car) => valueOf(car) === value).length,
                }))
                .filter((option) => option.count > 0),
        }))
        .filter((facet) => facet.options.length > 1);
}
